import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import io from 'socket.io-client';
import './embed.css';

const EmbedBottomTicker = () => {
  const [searchParams] = useSearchParams();

  // URL params for customization
  const bg = searchParams.get('bg') || '#000000';
  const speed = searchParams.get('speed') || 'medium';
  const height = searchParams.get('height') || '50px';
  const textColor = searchParams.get('color') || '#00ff41';
  const showLive = searchParams.get('live') !== 'false';
  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;

  const [events, setEvents] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef(null);
  const socketRef = useRef(null);

  const speedConfig = {
    slow: 30,
    medium: 50,
    fast: 80
  };

  // Connect to socket
  useEffect(() => {
    socketRef.current = io(API_BASE_URL, { auth: { public: true } });

    socketRef.current.on('market-event', (event) => {
      setEvents(prevEvents => [event, ...prevEvents].slice(0, 10));
    });

    socketRef.current.on('stock-updates', (data) => {
      if (Array.isArray(data)) {
        setStocks(prevStocks => {
          const updated = [...prevStocks];
          data.forEach(update => {
            const idx = updated.findIndex(s => s.id === update.id);
            if (idx >= 0) {
              updated[idx] = { ...updated[idx], currentPrice: update.price };
            }
          });
          return updated;
        });
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Fetch initial events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/events?limit=10`);
        if (response.ok) {
          const data = await response.json();
          setEvents(data.slice(0, 10));
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchEvents();
  }, []);

  // Fetch stock prices
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/stocks`);
        if (response.ok) {
          const data = await response.json();
          setStocks(data);
        }
      } catch (error) {
        console.error('Error fetching stocks:', error);
      }
    };

    fetchStocks();
    const interval = setInterval(fetchStocks, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Filter events to only show those from the last 30 minutes
  const getRecentEvents = () => {
    const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
    return events.filter(event => {
      const eventTime = new Date(event.timestamp || event.created_at).getTime();
      return eventTime > thirtyMinutesAgo;
    });
  };

  const formatEventText = (event) => {
    const eventTime = new Date(event.timestamp || event.created_at);
    const minutesAgo = Math.floor((Date.now() - eventTime.getTime()) / 60000);
    const timeAgo = minutesAgo < 1 ? 'now' : `${minutesAgo}m ago`;

    const humanizeType = (type) => {
      if (!type) return 'Event';
      return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bFda\b/i, 'FDA').replace(/\bCeo\b/i, 'CEO').replace(/\bCfo\b/i, 'CFO');
    };
    let description = event.description || event.message || humanizeType(event.type);

    let change = '';
    if (event.changePercent !== undefined && event.changePercent !== null) {
      const sign = event.changePercent > 0 ? '+' : '';
      change = ` (${sign}${event.changePercent}%)`;
    } else if (event.multiplier && event.multiplier !== 1) {
      const percent = ((event.multiplier - 1) * 100).toFixed(0);
      const sign = percent > 0 ? '+' : '';
      change = ` (${sign}${percent}%)`;
    }

    // Add affected stocks - convert stock IDs to ticker symbols
    let stocksText = '';
    if (event.affectedStocks && event.affectedStocks !== 'ALL' && event.affectedStocks !== 'all') {
      let affectedList = event.affectedStocks;

      // Convert to array if it's a string
      if (typeof affectedList === 'string') {
        affectedList = affectedList.split(',').map(s => s.trim()).filter(Boolean);
      }

      // Now process as array
      if (Array.isArray(affectedList) && affectedList.length > 0) {
        const firstStock = affectedList[0];
        if (firstStock !== 'ALL' && (typeof firstStock !== 'object' || firstStock.ticker !== 'ALL')) {
          const tickers = affectedList.map(stockId => {
            // If it's an object with ticker property
            if (typeof stockId === 'object' && stockId.ticker) {
              return stockId.ticker;
            }
            // If it's already a ticker (not starting with 'stock-'), return as-is
            if (typeof stockId === 'string' && !stockId.startsWith('stock-')) {
              return stockId;
            }
            // Look up the ticker from the stocks array
            const stockObj = stocks.find(s => s.id === stockId);
            return stockObj ? stockObj.ticker : null;
          }).filter(Boolean).slice(0, 3).join(', ');
          if (tickers) stocksText = ` [${tickers}]`;
        }
      }
    }

    return `${timeAgo}: ${description}${change}${stocksText}`;
  };

  const formatStock = (stock) => {
    const change = stock.basePrice ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100 : 0;
    const changeSymbol = change >= 0 ? '+' : '';
    return {
      ticker: stock.ticker,
      price: formatCurrency(stock.currentPrice),
      change: `${changeSymbol}${change.toFixed(1)}%`,
      isPositive: change >= 0
    };
  };

  // Build combined ticker items
  const buildTickerItems = () => {
    const items = [];
    const stockItems = stocks.map(stock => {
      const formatted = formatStock(stock);
      return {
        type: 'stock',
        text: `${formatted.ticker} ${formatted.price} ${formatted.change}`,
        isPositive: formatted.isPositive
      };
    });

    const recentEvents = getRecentEvents();
    const eventItems = recentEvents.map(event => {
      const isPositive = event.multiplier && event.multiplier > 1;
      return {
        type: 'event',
        text: formatEventText(event),
        isPositive
      };
    });

    // Interleave: add an event after every 3 stocks
    let eventIndex = 0;
    stockItems.forEach((stock, i) => {
      items.push(stock);
      if ((i + 1) % 3 === 0 && eventIndex < eventItems.length) {
        items.push(eventItems[eventIndex]);
        eventIndex++;
      }
    });

    // Add remaining events
    while (eventIndex < eventItems.length) {
      items.push(eventItems[eventIndex]);
      eventIndex++;
    }

    return items;
  };

  const tickerItems = buildTickerItems();

  const getAnimationDuration = () => {
    if (!tickerRef.current) return '60s';
    const contentWidth = tickerRef.current.scrollWidth;
    const pixelsPerSecond = speedConfig[speed] || speedConfig.medium;
    const duration = (contentWidth + window.innerWidth) / pixelsPerSecond;
    return `${duration}s`;
  };

  return (
    <div
      className="embed-bottom-ticker"
      style={{
        background: bg === 'transparent' ? 'transparent' : bg,
        height,
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        fontFamily: 'monospace',
        borderTop: `1px solid ${textColor}33`
      }}
      onClick={() => setIsPaused(!isPaused)}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={(e) => { e.stopPropagation(); document.documentElement.requestFullscreen?.(); }}>&#x26F6;</button>
      {showLive && (
        <div style={{
          background: '#ff0000',
          color: '#fff',
          padding: '4px 12px',
          fontWeight: 'bold',
          fontSize: '12px',
          letterSpacing: '2px',
          flexShrink: 0,
          zIndex: 10
        }}>
          LIVE
        </div>
      )}

      <div style={{
        flex: 1,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Fade edges */}
        <div style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '50px',
          background: `linear-gradient(90deg, ${bg === 'transparent' ? '#000' : bg} 0%, transparent 100%)`,
          zIndex: 5,
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '50px',
          background: `linear-gradient(90deg, transparent 0%, ${bg === 'transparent' ? '#000' : bg} 100%)`,
          zIndex: 5,
          pointerEvents: 'none'
        }} />

        <div
          ref={tickerRef}
          style={{
            display: 'flex',
            gap: '30px',
            whiteSpace: 'nowrap',
            animation: isPaused ? 'none' : `scroll-left ${getAnimationDuration()} linear infinite`
          }}
        >
          {/* Triple content for seamless loop */}
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, index) => (
            <span
              key={index}
              style={{
                color: item.isPositive ? '#00ff88' : '#ff4444',
                fontSize: '14px',
                fontWeight: item.type === 'event' ? 'bold' : 'normal',
                padding: item.type === 'event' ? '2px 8px' : '0',
                background: item.type === 'event' ? (item.isPositive ? '#00ff8815' : '#ff444415') : 'transparent',
                borderRadius: item.type === 'event' ? '4px' : '0'
              }}
            >
              {item.text}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes scroll-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
      `}</style>
    </div>
  );
};

export default EmbedBottomTicker;
